import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { LocalAuthService } from '../services/auth.service';
import { TenantService } from '../services/tenant.service';

export const tenantGuard: CanActivateFn = (route): Observable<boolean | UrlTree> => {
  const authService = inject(LocalAuthService);
  const tenantService = inject(TenantService);
  const router = inject(Router);

  const tenantId = route.paramMap.get('tenantId');

  return authService.isAuthenticated$.pipe(
    take(1),
    switchMap((isAuthenticated) => {
      if (!isAuthenticated) {
        return of(router.createUrlTree(['/home']));
      }

      // Pass the route tenant so a cold start / reload on `/:tenantId/...` selects it right away
      // instead of briefly activating the stored main tenant (which would fire the first API calls
      // — slides, screens, permissions — against the wrong tenant).
      return tenantService.loadTenants(false, tenantId).pipe(
        take(1),
        map((tenants) => {
          const exists = tenants.some((t) => t.tenantId === tenantId);
          if (!exists) {
            const first = tenants[0];
            return first
              ? router.createUrlTree(['/', first.tenantId, 'home'])
              : router.createUrlTree(['/home']);
          }
          tenantService.setActiveTenantId(tenantId);
          return true;
        }),
        catchError(() => of(router.createUrlTree(['/home']))),
      );
    }),
  );
};
